/**
 * Reality Correction Layer
 * ---------------------------------------------------------------
 * Surfaces synthetic vs real-world divergences and shows the
 * automatic generation-weight adjustments applied to
 * adaptive_learning_profiles by the live ingestion engine.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Scale, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { LIVE_TELEMETRY_EVENT } from "@/lib/platformIngestion";

interface ProfileRow {
  id: string;
  dimension: string;
  key: string;
  weight: number;
  trend_delta: number;
  sample_size: number;
  metadata: any;
  updated_at: string;
}

const titleCase = (s: string) => (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const RealityCorrectionLayer = () => {
  const [rows, setRows] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("adaptive_learning_profiles")
      .select("id, dimension, key, weight, trend_delta, sample_size, metadata, updated_at")
      .order("updated_at", { ascending: false })
      .limit(200);
    const filtered = ((data ?? []) as ProfileRow[]).filter(
      (r) => r.metadata?.reality_correction === true
    );
    setRows(filtered);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onIngest = () => void load();
    window.addEventListener(LIVE_TELEMETRY_EVENT, onIngest);
    return () => window.removeEventListener(LIVE_TELEMETRY_EVENT, onIngest);
  }, [load]);

  const summary = useMemo(() => {
    const corrections = rows.length;
    const upward = rows.filter((r) => r.trend_delta > 0).length;
    const downward = rows.filter((r) => r.trend_delta < 0).length;
    return { corrections, upward, downward };
  }, [rows]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="w-4 h-4 text-cyan" />
          Reality Correction Layer
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
            Synthetic bias correction · driven by platform telemetry
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
        ) : rows.length === 0 ? (
          <div className="text-[11px] text-muted-foreground font-mono text-center py-6">
            No reality corrections yet · ingest live telemetry to converge synthetic weights with platform truth
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Corrections Active</div>
                <div className="text-xl font-mono text-cyan mt-1">{summary.corrections}</div>
              </div>
              <div className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Upward Reweight</div>
                <div className="text-xl font-mono text-emerald-300 mt-1">{summary.upward}</div>
              </div>
              <div className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Downward Reweight</div>
                <div className="text-xl font-mono text-amber-300 mt-1">{summary.downward}</div>
              </div>
            </div>

            <ScrollArea className="max-h-[280px]">
              <div className="divide-y divide-glass-border">
                {rows.map((r) => {
                  const up = r.trend_delta >= 0;
                  const pctShift = Math.round(r.trend_delta * 100);
                  return (
                    <div key={r.id} className="flex items-center gap-3 py-2">
                      <Badge variant="outline" className="text-[9px] uppercase font-mono border-cyan/30 text-cyan/80">
                        {r.dimension}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-foreground truncate">
                          {titleCase(r.key)}
                          {r.metadata?.platform && (
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-2">
                              · {r.metadata.platform}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                          weight {r.weight.toFixed(2)} · sample {r.sample_size}
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-mono ${up ? "text-emerald-300" : "text-amber-300"}`}>
                        {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {up ? "+" : ""}{pctShift}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-mono text-center">
              Reality corrections feed adaptive_learning_profiles · future generation auto-adapts
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RealityCorrectionLayer;
